// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { connect } from "react-redux";
import { QueryRenderer, ReadyState } from "react-relay";
import { Helmet } from "react-helmet";
import MomentUtils from "@date-io/moment";

// chrome-wrapper imports
import { IRendererProps, IRendererContainer, IRendererState } from "./ChromeWrapper.d";
import * as s from "./ChromeWrapper.styles";
import { chromeWrapperRendererQuery } from "./ChromeWrapperRenderer.gql";
import { cwMuiBase } from "@clockwise/design-system";

// constants
import { paths } from "@constants/site.constant";

// components
import { SnackBar } from "../snack-bar/";
import { ChromeWrapper } from "@components/chrome-wrapper";
import { ChromeNav } from "@components/chrome-nav";
import {
  MAX_TIME_TO_RETRY,
  NetworkOffline,
} from "@clockwise/web-commons/src/components/network-offline";
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";

// util
import { ChromeAppWrapperView } from "@util/chrome-extension.util";
import { logger } from "@util/logger.util";
import { getOnlineStatus } from "@util/health.util";
import { isOnDisableHealthChecks } from "@util/experiment.util";
import { handleReadyStateError } from "@util/relay.util";
import { trackFSEvent } from "@util/analytics.util";
import { recordDuration } from "@util/stats.util";
import { windowLocation } from "@util/location.util";

// state
import { IReduxState } from "@state/reducers/root.reducer";
import { setIsOnline, setLastOffline } from "@state/actions/chrome-extension.actions";
import { snackBarOpen, snackBarClose } from "@state/actions/snack-bar.actions";

// material-ui
import { MuiThemeProvider, createTheme } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";

const muiTheme = createTheme(cwMuiBase);

class ChromeWrapperRendererCmpt extends React.PureComponent<IRendererProps, IRendererState> {
  private healthCheckTimer?: ReturnType<typeof setTimeout>;
  private lastCheckTimeout = 15000;
  private isRetrying = false;
  private retryCounts = 0;
  private appJwtCheckInterval?: ReturnType<typeof setInterval>;
  private didTrackReadyStateError = false;

  constructor(props: IRendererProps) {
    super(props);

    this.state = {
      boundaryError: false,
      wasInitiallyOffline: !props.isOnline,
    };
  }

  private maybeClearJwtCheckInterval = () => {
    if (this.appJwtCheckInterval) {
      clearInterval(this.appJwtCheckInterval);
    }
  };

  private storageChangeHandler = (event: any) => {
    // listen for jwt changes
    if (event.key === "Jwt") {
      if (event.newValue && !event.oldValue) {
        // sign in
        if (this.props.isChromeExtension) {
          windowLocation.reload("ChromeWrapperRendererJwtChrome");
        } else if (this.props.location) {
          const isLogin =
            this.props.location.pathname.startsWith(paths.login) ||
            this.props.location.pathname.startsWith(paths.signup) ||
            this.props.location.pathname.startsWith(paths.welcome);
          if (isLogin) {
            windowLocation.reload("ChromeWrapperRendererJwtLogin");
          }
        }
      } else if (event.oldValue && !event.newValue) {
        // sign out
        // PostMessageManager.openSidebar();
        windowLocation.reload("ChromeWrapperRendererJwtLogout");
      }
    }
  };

  private checkHealth = () => {
    if (this.isRetrying) {
      return Promise.resolve();
    }

    if (this.healthCheckTimer) {
      clearTimeout(this.healthCheckTimer);
      delete this.healthCheckTimer;
    }

    this.isRetrying = true;

    return getOnlineStatus().then((isOnline) => {
      if (isOnline) {
        if (!this.props.isOnline) {
          this.props.setIsOnline(true);
          recordDuration("client.reconnected", this.retryCounts, { project: "chrome" });
        }
        this.healthCheckTimer = setTimeout(this.checkHealth, 10000);
        if (this.state.wasInitiallyOffline) {
          this.setState({ wasInitiallyOffline: false });
        }
      } else {
        if (this.props.isOnline) {
          this.props.setIsOnline(false);
          this.props.setLastOffline(new Date().valueOf());
          this.retryCounts = 0;
        }
        this.retryCounts++;
        if (!this.state.wasInitiallyOffline) {
          this.healthCheckTimer = setTimeout(this.checkHealth, this.lastCheckTimeout);
          this.lastCheckTimeout = Math.min(this.lastCheckTimeout * 2, MAX_TIME_TO_RETRY);
        }
      }

      this.isRetrying = false;
    });
  };

  public componentDidMount() {
    if (!isOnDisableHealthChecks()) {
      this.checkHealth();
    }

    // listen for storage changes to detect sign in
    window.addEventListener("storage", this.storageChangeHandler, false);
  }

  public componentWillUnmount() {
    if (this.healthCheckTimer) {
      clearTimeout(this.healthCheckTimer);
      delete this.healthCheckTimer;
    }

    this.maybeClearJwtCheckInterval();
    window.removeEventListener("storage", this.storageChangeHandler, false);
  }

  public componentDidCatch(error: Error, info: React.ErrorInfo) {
    logger.error("componentDidCatch in ChromeWrapperRenderer", error, info);

    trackFSEvent("ChromeWrapper Did Catch", {
      errorMessage: error.message,
      errorStack: info.componentStack,
    });

    // Display fallback UI
    this.setState({ boundaryError: true });
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IRendererProps) {
    if (!nextProps.isOnline && this.props.isOnline) {
      this.lastCheckTimeout = 15000 + Math.random() * 45000;
      this.props.snackBarOpen(
        "Oops, we're having trouble connecting to Clockwise right now. We'll keep trying to reconnect.",
        "warning",
        true,
        "Retry",
        this.checkHealth,
        undefined,
        null,
      );
    } else if (nextProps.isOnline && !this.props.isOnline) {
      this.props.snackBarClose();
    }
  }

  public renderError() {
    const viewer = {} as ISchema.IViewer;
    return <ChromeWrapper viewer={viewer} fatalError={true} />;
  }

  public renderContent() {
    const { environment } = this.props;

    if (this.state.wasInitiallyOffline) {
      return (
        <div style={s.networkOffline.container}>
          <ChromeNav
            authed={false}
            view={ChromeAppWrapperView.NoAuth}
            previousView={null}
            environment={null}
            org={null}
          />
          <div style={s.networkOffline.prompt}>
            <NetworkOffline onRetry={this.checkHealth} />
          </div>
        </div>
      );
    }

    return (
      <QueryRenderer
        environment={environment}
        query={chromeWrapperRendererQuery}
        variables={{}}
        cacheConfig={{}}
        render={(readyState: ReadyState) => {
          // check for error or show a loader for first load
          if (this.state.boundaryError) {
            return this.renderError();
          }

          const freshViewer = readyState.props && readyState.props.viewer;

          if (
            readyState.error &&
            handleReadyStateError(readyState.error, "ChromeWrapperRenderer")
          ) {
            if (!this.didTrackReadyStateError) {
              logger.error("ChromeWrapperRenderer readyStateError", readyState.error);
              this.didTrackReadyStateError = true;
            }
            return this.renderError();
          }

          // fetched viewer is present
          if (freshViewer) {
            return <ChromeWrapper viewer={freshViewer} />;
          }

          // loading for the first time
          return (
            <div style={s.offlineContainer}>
              <CircularProgress />
            </div>
          );
        }}
      />
    );
  }

  public render() {
    const description =
      "Clockwise optimizes your team's calendars to create more time in everyone's day. Get started for free.";

    return (
      <MuiThemeProvider theme={muiTheme}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <div style={s.rootContainer}>
            <Helmet>
              <title>Clockwise: Make time for what matters</title>
              <meta name="description" content={description} />
              <meta property="og:description" content={description} />
              <meta name="twitter:description" content={description} />
              <meta itemProp="description" content={description} />
            </Helmet>
            {this.renderContent()}
            <SnackBar />
          </div>
        </MuiPickersUtilsProvider>
      </MuiThemeProvider>
    );
  }
}

export class ChromeWrapperRendererLight extends React.Component<
  IRendererContainer,
  IRendererState
> {
  private didTrackReadyStateError = false;

  constructor(props: IRendererContainer) {
    super(props);

    this.state = {
      wasInitiallyOffline: false,
      boundaryError: false,
    };
  }

  public componentDidCatch(error: Error, info: React.ErrorInfo) {
    logger.error("componentDidCatch in ChromeWrapperRendererLight", error, info);

    trackFSEvent("ChromeWrapperLight Did Catch", {
      errorMessage: error.message,
      errorStack: info.componentStack,
    });

    // Display fallback UI
    this.setState({ boundaryError: true });
  }

  public renderError() {
    const viewer = {} as ISchema.IViewer;
    return <ChromeWrapper viewer={viewer} fatalError={true} />;
  }

  public render() {
    const { environment } = this.props;

    return (
      <QueryRenderer
        environment={environment}
        query={chromeWrapperRendererQuery}
        variables={{}}
        cacheConfig={{}}
        render={(readyState: ReadyState) => {
          if (this.state.boundaryError) {
            return this.renderError();
          }

          const freshViewer = readyState.props && readyState.props.viewer;

          if (
            readyState.error &&
            handleReadyStateError(readyState.error, "ChromeWrapperRendererLight")
          ) {
            if (!this.didTrackReadyStateError) {
              logger.error("ChromeWrapperRendererLight readyStateError", readyState.error);
              this.didTrackReadyStateError = true;
            }
            return this.renderError();
          }

          // fetched viewer is present
          if (freshViewer) {
            return (
              <ChromeWrapper
                viewer={freshViewer}
                match={this.props.match}
                router={this.props.router}
              />
            );
          }

          // loading for the first time
          return (
            <div style={s.offlineContainer}>
              <CircularProgress />
            </div>
          );
        }}
      />
    );
  }
}

///////////
// Redux //
///////////

function mapStateToProps(state: IReduxState, _ownProps: IRendererContainer) {
  return {
    isOnline: state.webExtension.isOnline,
    isChromeExtension: state.webExtension.isWebExtension,
    mode: state.webExtension.mode,
  };
}

export const ChromeWrapperRenderer = connect(mapStateToProps, {
  setIsOnline,
  setLastOffline,
  snackBarOpen,
  snackBarClose,
})(ChromeWrapperRendererCmpt);
